import React, { useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
	Edit,
	SavedSearchesSkeleton,
	TrashFull
} from '@/resources/assets/images';
import type { RootState } from '@/rootReducer';
import './SavedSearchesTab.scss';
import type { Search } from '@/modules/search/searchTypes';
import Button from '@/components/Button/newButtons';
import {
	searchEditSearchRequest,
	searchRemoveSearchRequest
} from '@/modules/search/searchActions';

import {
	BusinessQuery,
	FilterQueryType,
	SearchType,
	Search as DashboardSearch
} from '@/modules/dashboard/dashboardTypes';
import { sendAnalytics } from '@/services/analyticsService';
import { cn } from '@/lib/classNames';
import { Dialog } from '@/components/atoms/Dialog';
import { useSearchTabContext } from '../SearchTab/SearchTabContext'; // Import the context

// Props for SavedSearchesTab
interface SavedSearchesTabProps {
	onChangeTabToSearch: () => void;
	onFilterChange: (businessQuery: BusinessQuery) => void;
	onGetBusiness: (businessQuery: BusinessQuery) => void;
}

const SavedSearchesTab: React.FC<SavedSearchesTabProps> = ({
	onChangeTabToSearch,
	onFilterChange,
	onGetBusiness
}) => {
	const [savedSearches, query, user] = useSelector(
		(state: RootState) => [
			state.search.get('data').toJS(),
			state.dashboard.get('query').toJS(),
			state.user.getIn(['authorize', 'user']).toJS()
		],
		shallowEqual
	);

	const { setSelectedLocation } = useSearchTabContext();

	const normalizeCoordinates = (coords: string | number[]) =>
		typeof coords === 'string' ? coords : JSON.stringify(coords);

	const splitQuerySearches = (queryNew: BusinessQuery) => {
		if (!queryNew?.searches) return { includes: [], excludes: [] };

		const includes: string[] = [];
		const excludes: string[] = [];

		queryNew.searches?.forEach((searchParam: DashboardSearch) => {
			if (searchParam.type === SearchType.INCLUDES) {
				includes.push(searchParam.value);
			} else if (searchParam.type === SearchType.EXCLUDES) {
				excludes.push(searchParam.value);
			}
		});

		return { includes, excludes };
	};

	const parseLocation = (search: Search, queryNew: BusinessQuery) => {
		if (
			!search.location?.coordinates[0] ||
			!search.location?.coordinates[1]
		)
			return {
				coordinates: undefined,
				sortField: undefined,
				sort: undefined
			};

		const coordinates = normalizeCoordinates(
			search.location?.coordinates
		) as string;

		if (queryNew.sort || queryNew.listId)
			return { coordinates, sortField: undefined, sort: undefined };

		const sortField = 'score';

		const sort = { score: -1 };

		return { coordinates, sortField, sort };
	};

	const updateQuery = (search: Search) => {
		const { includes, excludes } = splitQuerySearches({ searches: search.params });
		const { coordinates, sortField, sort } = parseLocation(search, query);

		const queryNew: BusinessQuery = {
			...query,
			...search.filters,
			coordinates,
			sortField,
			sort,
			searches: search.params,
			count: true,
			includes,
			excludes,
			queryType: FilterQueryType.SAVED_SEARCH,
			limit: 50,
			skip: 0
		};

		// Set selected location string for display purposes
		queryNew.selectedLocation = search.locationName as string;

		setSelectedLocation(search.locationName as string);

		onFilterChange(queryNew);
		onGetBusiness(queryNew);

		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: 'User selected a saved search',
			properties: {
				query: queryNew
			}
		});

		onChangeTabToSearch();
	};

	const [selectedSearch, setSelectedSearch] = useState<Search | null>(null);
	const [isEditModalOpen, setEditModalOpen] = useState(false);
	const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);

	const handleSelectSearch = (search: Search | null) => {
		setSelectedSearch(search);
	};

	const handleToggleEditNameModal = (open: boolean, search?: Search) => {
		setEditModalOpen(open);

		handleSelectSearch(open && search ? search : null);
	};

	const handleToggleDeleteModal = (open: boolean, search?: Search) => {
		setDeleteModalOpen(open);

		handleSelectSearch(open && search ? search : null);
	};

	return (
		<>
			{savedSearches?.length > 0 ? (
				<ul className="absolute bottom-0 left-0 right-0 top-0 flex flex-1 flex-col">
					{savedSearches.map((search: Search) => (
						<li
							className={cn(
								'group/saved-search relative inline-flex min-h-14 items-center border-b border-dm-gray text-sm text-dm-white transition-colors',
								'hover:bg-dm-black4'
							)}
							key={search.id}
						>
							<button
								type="button"
								onClick={() => updateQuery(search)}
								className="h-full w-full text-ellipsis whitespace-nowrap p-4 text-start"
							>
								{search.name}
							</button>

							<div
								className={cn(
									'pointer-events-none absolute right-4 top-1/2 flex -translate-y-1/2 items-center gap-2 rounded border border-dm-gray bg-dm-black4 p-1 transition-opacity lg:opacity-0',
									'group-hover/saved-search:pointer-events-auto group-hover/saved-search:opacity-100'
								)}
							>
								<button
									type="button"
									onClick={() =>
										handleToggleEditNameModal(true, search)
									}
									className="flex aspect-square items-center justify-center rounded bg-dm-black3.5 px-2 py-1 text-dm-gold"
								>
									<Edit
										className="saved-searches-tab-list-item-icon"
										height={12}
										width={12}
									/>
								</button>

								<button
									type="button"
									onClick={() =>
										handleToggleDeleteModal(true, search)
									}
									className="flex aspect-square items-center justify-center rounded bg-dm-black3.5 px-2 py-1 text-dm-gold"
								>
									<TrashFull
										className="saved-searches-tab-list-item-icon"
										height={12}
										width={12}
									/>
								</button>
							</div>
						</li>
					))}
				</ul>
			) : (
				<div className="flex flex-col items-center justify-center gap-4 px-5 py-3">
					<p className="text-center text-sm font-light leading-5 text-dm-white">
						Apply filters from the Search tab and click &apos;Saved
						Searches&apos; to save your filter configurations.
						Easily reapply these filters in the future.
					</p>

					<SavedSearchesSkeleton />
				</div>
			)}

			<EditSearchNameModal
				search={selectedSearch}
				isOpen={isEditModalOpen}
				onToggleModal={handleToggleEditNameModal}
			/>

			<DeleteSavedSearchModal
				search={selectedSearch}
				isOpen={isDeleteModalOpen}
				onToggleModal={handleToggleDeleteModal}
			/>
		</>
	);
};

type EditSearchNameModalProps = {
	search: Search | null;
	isOpen: boolean;
	onToggleModal: (open: boolean) => void;
};

const EditSearchNameModal = ({
	search,
	isOpen,
	onToggleModal
}: EditSearchNameModalProps) => {
	const inputRef = useRef<HTMLInputElement>(null);

	const dispatch = useDispatch();

	if (!search) return null;

	const editSearch = (s: Search) => dispatch(searchEditSearchRequest(s));

	const handleEditSavedSearchName = () => {
		if (!inputRef.current?.value) return;

		editSearch({
			...search,
			name: inputRef.current?.value
		});

		onToggleModal(false);
	};

	return (
		<Dialog.Root open={isOpen} onOpenChange={onToggleModal}>
			<Dialog.Portal>
				<Dialog.Content className="gap-8">
					<h1 className="text-3xl font-light">
						Edit saved filters name
					</h1>

					<section className="flex flex-col gap-4">
						<label
							htmlFor="edit-search-name"
							className="font-medium text-dark-grey"
						>
							Name
						</label>
						<input
							className="w-full"
							id="edit-search-name"
							ref={inputRef}
							defaultValue={search.name}
						/>
					</section>

					<section className="flex w-full justify-end gap-4">
						<Dialog.Close asChild>
							<Button variant="text">Cancel</Button>
						</Dialog.Close>

						<Button
							variant="secondary"
							onClick={handleEditSavedSearchName}
						>
							Save
						</Button>
					</section>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
};

type DeleteSavedSearchModalProps = {
	search: Search | null;
	isOpen: boolean;
	onToggleModal: (open: boolean) => void;
};

const DeleteSavedSearchModal = ({
	search,
	isOpen,
	onToggleModal
}: DeleteSavedSearchModalProps) => {
	const dispatch = useDispatch();

	if (!search) return null;

	const removeSavedSearch = (s: Search) =>
		dispatch(searchRemoveSearchRequest(s));

	const handleDeleteSavedSearch = () => {
		removeSavedSearch(search);

		onToggleModal(false);
	};

	return (
		<Dialog.Root open={isOpen} onOpenChange={onToggleModal}>
			<Dialog.Portal>
				<Dialog.Content className="gap-8">
					<h1 className="text-3xl font-light">Are you sure?</h1>

					<section className="flex flex-col gap-4">
						<p>This saved filters will be permanently removed.</p>
					</section>

					<section className="flex w-full justify-end gap-4">
						<Dialog.Close asChild>
							<Button variant="text">Cancel</Button>
						</Dialog.Close>

						<Button
							variant="secondary"
							leftIcon={<TrashFull />}
							red
							onClick={handleDeleteSavedSearch}
						>
							Confirm Deletion
						</Button>
					</section>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
};

export default SavedSearchesTab;
